// Mock storage utilities for development
export const uploadFile = async (file, path) => {
  try {
    // Validate file
    if (!file || !file.name) {
      throw new Error('Invalid file')
    }

    // Validate path
    if (!path) {
      throw new Error('Invalid file path')
    }

    // Create a mock URL for demo purposes
    const fileExtension = file.name.split('.').pop()
    const mockUrl = `https://firebasestorage.googleapis.com/v0/b/quran-class-portal/o/${encodeURIComponent(path)}?alt=media&token=${Date.now()}`
    
    // Simulate upload delay based on file size
    const delay = Math.min(Math.max(file.size / 1024 / 1024 * 100, 500), 3000)
    await new Promise(resolve => setTimeout(resolve, delay))
    
    // Simulate occasional failures for testing
    if (Math.random() < 0.05) { // 5% chance of failure
      throw new Error('Network error during upload')
    }

    return {
      success: true,
      url: mockUrl,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      path: path
    }
  } catch (error) {
    console.error('Error uploading file:', error)
    return {
      success: false,
      error: error.message,
      fileName: file?.name || 'Unknown file'
    }
  }
}

export const deleteFile = async (path) => {
  try {
    if (!path) {
      throw new Error('Invalid file path')
    }

    // Simulate delete delay
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Simulate occasional failures
    if (Math.random() < 0.05) {
      throw new Error('Network error during deletion')
    }

    return {
      success: true,
      path: path
    }
  } catch (error) {
    console.error('Error deleting file:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

export const generateFilePath = (classNumber, type, fileName) => {
  try {
    const timestamp = Date.now()
    const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_')
    const fileExtension = sanitizedFileName.split('.').pop()
    const baseName = sanitizedFileName.replace(`.${fileExtension}`, '')
    
    return `classes/${classNumber}/${type}/${timestamp}_${baseName}.${fileExtension}`
  } catch (error) {
    console.error('Error generating file path:', error)
    return `classes/unknown/${type}/${Date.now()}_file`
  }
}

// Utility function to validate URLs
export const isValidUrl = (string) => {
  try {
    new URL(string)
    return string.startsWith('http://') || string.startsWith('https://')
  } catch (_) {
    return false
  }
}

// Utility function to extract file extension from URL
export const getFileExtensionFromUrl = (url) => {
  try {
    const urlObj = new URL(url)
    const pathname = urlObj.pathname
    const extension = pathname.split('.').pop()
    return extension.toLowerCase()
  } catch (_) {
    return ''
  }
}

// Utility function to validate image URL
export const isValidImageUrl = (url) => {
  if (!isValidUrl(url)) return false
  const extension = getFileExtensionFromUrl(url)
  const validExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']
  return validExtensions.includes(extension)
}

// Utility function to validate audio URL
export const isValidAudioUrl = (url) => {
  if (!isValidUrl(url)) return false
  const extension = getFileExtensionFromUrl(url)
  const validExtensions = ['mp3', 'wav', 'ogg', 'm4a', 'aac']
  return validExtensions.includes(extension)
}

// S3 prefix is not used in this implementation
export const getS3Prefix = () => {
  return 'classes/' // Default prefix for organization
}